<script lang="ts" setup>
import SvgLoader from '@/utils/SvgLoader.vue';
import { computed } from 'vue';
import { RouterItem } from './interfaces/routerItems';
import { i18n } from '@/i18n';
import { removeTokenIfExpired } from '@/appLogic/tokenAroundManagement';
import { useRouter } from 'vue-router';
import { displaySessionErrorInfo } from '@/corePagesModule/components/InfoModal/displaySessionErrorInfo';
import Mdi from '@/corePagesModule/components/Mdi.vue';

const t = i18n.t;
const router = useRouter();

const props = defineProps<{
  routeItem: RouterItem;
  isRoot: boolean;
  isOpenChildren?: boolean;
}>();

const emits = defineEmits<{
  (e: 'onClickExpansionToggle'): void;
}>();

const computedLinkPath = computed((): string => {
  return props.routeItem.expandChildren && props.isRoot ? '' : props.routeItem.to;
});

const onClickExpandedItem = (): void => {
  if (props.routeItem.expandChildren && props.isRoot) {
    emits('onClickExpansionToggle');
  }
};

const handleClick = async (child?: RouterItem) => {
  if (!child) return;
  // ここでトークンチェック（Storeや関数を呼ぶ）
  const ok = await removeTokenIfExpired();

  if (ok) {
    router.push(child.to); // 正常ならそのまま遷移
  } else {
    // トークンが期限切れの場合はモーダルを表示してログイン画面に遷移させる
    await displaySessionErrorInfo();
  }
};
</script>

<template>
  <div class="sidebar-item-container">
    <router-link
      :data-testid="routeItem.to == '/translate-excel' ? `sidebar-content_inventory_information` : ''"
      :to="computedLinkPath"
      @click="onClickExpandedItem"
      @click.prevent="handleClick(routeItem)"
    >
      <div class="sidebar-item-content">
        <div v-if="isRoot && routeItem.icon" class="sidebar-item-content__icon-wrapper">
          <SvgLoader v-if="routeItem.icon.startsWith('sidebar-logo')" class="sidebar-item-content__icon-image" :name="routeItem.icon" />
          <Mdi v-else class="sidebar-item-content__icon-image" :icon="routeItem.icon" tag="div" />
          <span v-if="routeItem.badge && routeItem.badge !== ''" class="sidebar-item-badge">{{ routeItem.badge }}</span>
        </div>
        <i18n-t
          class="sidebar-item-content__label-text i18n-break"
          :class="{ 'sidebar-item-content__label-text--child-item': !isRoot }"
          :keypath="routeItem.longName ? routeItem.longName : routeItem.name"
          tag="p"
        >
          <template #newLine>
            <br />
          </template>
        </i18n-t>
        <div v-if="routeItem.expandChildren && isRoot" class="sidebar-item-content__expand-icon">
          <SvgLoader v-if="props.isOpenChildren" class="sidebar-item-content__expand-icon--opened" name="arrowDown" />
          <SvgLoader v-else class="sidebar-item-content__expand-icon--closed" name="chevronRight" />
        </div>
      </div>
    </router-link>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-item-content {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 20px;
  place-items: center;
  gap: 8px;
  padding-left: 8px;

  &__icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon-image {
    display: block;
    width: 24px;
    color: $textSecondary;
  }

  &__label-text {
    width: 100%;
    font: Noto Sans JP;
    font-size: $fontSizeSS;
    font-weight: 700;
    letter-spacing: 0;
    color: $textSecondary;
    white-space: pre-line;
    text-align: left;

    &--child-item {
      grid-column: 2 / 4;
    }
  }

  &__expand-icon {
    display: grid;
    place-content: center;
    width: 20px;
    height: 20px;
  }
}

a {
  &:hover,
  &:focus,
  &:focus-visible {
    color: inherit !important;
    background-color: inherit !important;
  }
}

.sidebar-item-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background-color: $semanticError;
  color: $semanticWhite;
  border-radius: 9px;
  font-family: Noto Sans JP;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>
